/**
 * Mistake Review Service
 * Intelligent wrong-answer tracking and review system
 */

export interface MistakeRecord {
  id: string;
  type: 'vocabulary' | 'speaking' | 'listening' | 'game';
  contentId: string;
  content: string; // the actual question/word
  userAnswer: string; // what user answered
  correctAnswer: string; // the correct answer
  wrongCount: number; // how many times wrong
  lastWrongDate: string;
  reviewed: boolean; // has been reviewed
  reviewSuccess: boolean; // did they get it right in review
  mastery: number; // 0-5, mastery level
}

export interface MistakeReviewSession {
  date: string;
  reviewed: string[]; // mistake IDs reviewed
  correct: number;
  total: number;
}

const STORAGE_KEY = 'english_tutor_mistakes';

export class MistakeReviewService {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  private getKey(): string {
    return `${STORAGE_KEY}_${this.userId}`;
  }

  // Load mistakes
  private loadMistakes(): MistakeRecord[] {
    const data = localStorage.getItem(this.getKey());
    return data ? JSON.parse(data) : [];
  }

  // Save mistakes
  private saveMistakes(mistakes: MistakeRecord[]): void {
    localStorage.setItem(this.getKey(), JSON.stringify(mistakes));
  }

  // Add or update a mistake
  addMistake(
    type: MistakeRecord['type'],
    contentId: string,
    content: string,
    userAnswer: string,
    correctAnswer: string
  ): MistakeRecord {
    const mistakes = this.loadMistakes();
    const today = new Date().toISOString().split('T')[0];

    // Check if this mistake already exists
    const existing = mistakes.find(m => m.contentId === contentId && m.type === type);

    if (existing) {
      existing.wrongCount += 1;
      existing.lastWrongDate = today;
      existing.userAnswer = userAnswer;
      existing.reviewed = false;
      existing.mastery = Math.max(0, existing.mastery - 1);
      this.saveMistakes(mistakes);
      return existing;
    }

    // Create new mistake record
    const newMistake: MistakeRecord = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      contentId,
      content,
      userAnswer,
      correctAnswer,
      wrongCount: 1,
      lastWrongDate: today,
      reviewed: false,
      reviewSuccess: false,
      mastery: 0,
    };

    mistakes.push(newMistake);
    this.saveMistakes(mistakes);
    return newMistake;
  }

  // Get mistakes for review (sorted by frequency and date)
  getMistakesForReview(limit: number = 10): MistakeRecord[] {
    const mistakes = this.loadMistakes();

    // Filter: not reviewed or review failed
    const toReview = mistakes.filter(m => !m.reviewed || !m.reviewSuccess);

    // Sort by: wrong count (descending), then last wrong date (recent first)
    toReview.sort((a, b) => {
      if (a.wrongCount !== b.wrongCount) {
        return b.wrongCount - a.wrongCount;
      }
      return new Date(b.lastWrongDate).getTime() - new Date(a.lastWrongDate).getTime();
    });

    return toReview.slice(0, limit);
  }

  // Mark mistake as reviewed
  markReviewed(mistakeId: string, success: boolean): void {
    const mistakes = this.loadMistakes();
    const mistake = mistakes.find(m => m.id === mistakeId);

    if (mistake) {
      mistake.reviewed = true;
      mistake.reviewSuccess = success;

      if (success) {
        mistake.mastery += 1;

        // Remove from mistakes if mastery >= 3
        if (mistake.mastery >= 3) {
          const index = mistakes.findIndex(m => m.id === mistakeId);
          if (index !== -1) {
            mistakes.splice(index, 1);
          }
        }
      }

      this.saveMistakes(mistakes);
    }
  }

  // Get statistics
  getStatistics(): {
    total: number;
    reviewed: number;
    mastered: number;
    mostCommon: MistakeRecord[];
  } {
    const mistakes = this.loadMistakes();

    return {
      total: mistakes.length,
      reviewed: mistakes.filter(m => m.reviewed).length,
      mastered: mistakes.filter(m => m.mastery >= 3).length,
      mostCommon: mistakes.sort((a, b) => b.wrongCount - a.wrongCount).slice(0, 5),
    };
  }

  // Clear all mistakes (for testing)
  clearAll(): void {
    localStorage.removeItem(this.getKey());
  }

  // Export mistakes as text
  exportAsText(): string {
    const mistakes = this.loadMistakes();

    if (mistakes.length === 0) {
      return '暂无错题记录 🎉';
    }

    let text = `错题本 (${mistakes.length}条)\n\n`;

    mistakes.forEach(m => {
      text += `${m.content}\n`;
      text += `你的答案：${m.userAnswer}\n`;
      text += `正确答案：${m.correctAnswer}\n`;
      text += `错误次数：${m.wrongCount}次\n`;
      text += `掌握程度：${m.mastery}/5\n`;
      text += `---\n`;
    });

    return text;
  }
}

// Singleton instance
export let mistakeReviewService: MistakeReviewService | null = null;

export function initMistakeReviewService(userId: string): void {
  mistakeReviewService = new MistakeReviewService(userId);
}